import './CardPredictionContract.css'
import {
  getSelectionProviderKey,
  getSelectionProviderPayload,
  selectionDisplayName,
  selectionEventName,
} from '../../utils/selectionSet'
import type { Selection } from '../../types/feedContract'

/** First non-empty string among unknown-typed payload/map values. */
function firstText(...values: unknown[]): string {
  for (const value of values) {
    if (typeof value === 'string' && value) return value
  }
  return ''
}

function parsePercent(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null
  if (typeof value === 'string') {
    const normalized = value.replace('%', '').trim()
    if (!normalized) return null
    const parsed = Number(normalized)
    if (!Number.isFinite(parsed)) return null
    return parsed <= 1 ? parsed * 100 : parsed
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value <= 1 ? value * 100 : value
  }
  return null
}

function percentFromSelection(selection: Selection, payload: Record<string, unknown>): number | null {
  return (
    parsePercent(payload.chance) ??
    parsePercent(payload.yes_price) ??
    parsePercent(selection?.chance)
  )
}

function displayPercent(value: number | null): string {
  return value === null ? '--' : `${Math.round(value)}%`
}

function ogDirection(currentSelection: Selection, payload: Record<string, unknown>): string {
  return (
    selectionDisplayName(currentSelection) ||
    firstText(payload.participant_name, payload.team_name, payload.team_abbrev, payload.contract_title) ||
    'Trade'
  )
}

function cryptoDirection(currentSelection: Selection, payload: Record<string, unknown>): string {
  return (
    firstText(payload.team_abbrev, payload.team_name, payload.participant_name) ||
    selectionDisplayName(currentSelection) ||
    'Trade'
  )
}

interface CardPredictionContractProps {
  selection: Selection
}

export function CardPredictionContract({ selection }: CardPredictionContractProps) {
  const payload = getSelectionProviderPayload(selection)
  const providerKey = getSelectionProviderKey(selection)
  const isOG = providerKey === 'og'
  const classPrefix = isOG ? 'og-prediction' : 'crypto-prediction'
  const actionVerb = isOG ? 'Trade' : 'Buy'
  const eventName =
    selectionEventName(selection) || firstText(payload.event_name, payload.event_title) || 'Prediction market'
  const marketName =
    firstText(payload.market_title, selection?.market?.name, payload.market_type) || 'Winner'
  const direction = isOG ? ogDirection(selection, payload) : cryptoDirection(selection, payload)
  const percent = percentFromSelection(selection, payload)
  const chanceWidth = percent === null ? 0 : Math.max(0, Math.min(100, percent))

  return (
    <div className={`${classPrefix}-wrap`}>
      <div className={`${classPrefix}-card`} aria-label={`${eventName} ${actionVerb} ${direction} ${marketName} ${displayPercent(percent)}`}>
        <div className={`${classPrefix}-event`}>
          {isOG && <span className="og-prediction-provider">OG</span>}
          <span>{eventName}</span>
        </div>
        <div className={`${classPrefix}-row`}>
          <div className={`${classPrefix}-copy`}>
            <div className={`${classPrefix}-direction`}>{actionVerb} {direction}</div>
            <div className={`${classPrefix}-market`}>{marketName}</div>
          </div>
          <div className={`${classPrefix}-percent`}>
            {displayPercent(percent)}
            {isOG && <span className="og-prediction-side">Yes</span>}
          </div>
        </div>
        <div className={`${classPrefix}-chance`} aria-hidden="true">
          <span className={`${classPrefix}-chance-fill`} style={{ width: `${chanceWidth}%` }} />
          <span className={`${classPrefix}-chance-rest`} />
        </div>
      </div>
    </div>
  )
}
